import React, { useEffect, useState } from "react";
import useAppSelector from "hooks/useAppSelector";
import { selectPollingState } from "features/picture/picturePollingSlice";
import LoadingIcon from "assets/icons/LoadingIcon";

interface PictureThumbnailProps {
  imageSrc: string;
  name: string;
  pictureId?: number;
  isProcessing?: boolean; // For regular job_status based processing
  onConversionComplete?: () => void; // Callback when polling completes
  inDialogContext?: boolean; // Flag to indicate if this is in a PictureDialog
  rounded?: boolean;
  hoverBrighten?: boolean;
}

const PictureThumbnail: React.FC<PictureThumbnailProps> = ({
  imageSrc,
  name,
  pictureId,
  isProcessing = false,
  onConversionComplete,
  inDialogContext = false,
  rounded = true,
  hoverBrighten = false,
}) => {
  const pollingState = useAppSelector((state) =>
    pictureId ? selectPollingState(state, pictureId) : undefined
  );

  // Track polling state locally to ensure UI updates
  const [localPollingState, setLocalPollingState] = useState({
    isPolling: false,
    wasPolling: false,
  });

  const isPolling = pollingState?.isPolling ?? localPollingState.isPolling;
  const pollingError = pollingState?.error ?? null;

  // Handle Redux polling state changes
  useEffect(() => {
    if (pollingState?.isPolling) {
      // Set both Redux and local polling state
      setLocalPollingState((prev) => ({
        ...prev,
        isPolling: true,
        wasPolling: true,
      }));
      sessionStorage.setItem(`polling_${pictureId}`, "true");
    } else if (
      pollingState &&
      !pollingState.isPolling &&
      localPollingState.wasPolling
    ) {
      // Only trigger completion if we were previously polling
      setLocalPollingState((prev) => ({ ...prev, isPolling: false }));

      // Update the image if needed without triggering a full refresh
      // We'll only call onConversionComplete if we're not in a dialog context
      // This prevents reloading the dialog when polling completes
      if (!pollingState.error && onConversionComplete && !inDialogContext) {
        onConversionComplete();
      }

      // Clear session storage
      sessionStorage.removeItem(`polling_${pictureId}`);
    }
  }, [
    pollingState,
    pictureId,
    onConversionComplete,
    localPollingState.wasPolling,
    inDialogContext,
  ]);

  // Check session storage on mount to restore polling state
  useEffect(() => {
    if (pictureId) {
      const wasPolling =
        sessionStorage.getItem(`polling_${pictureId}`) === "true";
      if (wasPolling) {
        setLocalPollingState({ isPolling: true, wasPolling: true });
      }
    }

    return () => {
      // Cleanup session storage on unmount if we're done polling
      if (pictureId && !isPolling) {
        sessionStorage.removeItem(`polling_${pictureId}`);
      }
    };
  }, [pictureId, isPolling]);

  // We handle processing and polling separately in the render

  const roundedClass = rounded ? "rounded-md" : "rounded-none";
  const imageHoverClass = hoverBrighten
    ? "transition-[filter] duration-200 ease-out hover:brightness-110"
    : "";

  return (
    <div className={`w-full h-full ${roundedClass} relative`}>
      <img
        className={`min-w-[100%] h-[100%] ${roundedClass} ${imageHoverClass}`}
        src={
          isPolling
            ? "/images/default_thumbnail.svg"
            : imageSrc || "/images/default_thumbnail.svg"
        }
        alt={name}
      />
      {isProcessing && !isPolling && (
        <div
          className={`w-full h-full absolute top-0 left-0 z-10 bg-black/25 ${roundedClass}`}
        >
          <div className="w-1/2 h-1/2 absolute left-[50%] -translate-x-[50%] top-[50%] -translate-y-[50%]">
            <LoadingIcon />
          </div>
        </div>
      )}
      {pollingError && (
        <div className="absolute bottom-0 left-0 right-0 bg-red-500/75 text-white text-xs p-1 text-center">
          {pollingError}
        </div>
      )}
    </div>
  );
};

export default PictureThumbnail;
